import { useEffect, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { IconArrowRight, IconDuplicate, IconTrash, IconX } from './icons';
import { MenuAnchor, MenuItem, MenuLabel, MenuSurface, useDismiss } from './Menu';
import { useToast } from './toast';

/**
 * The fixed dark toolbar at the bottom of a collection page (and, in a
 * simpler form, the Usage table). 52px, radius 12, #0f0f10. Move and
 * Duplicate menus are mutually exclusive and open upward; Esc clears.
 * While it is mounted the toast lifts to 90px.
 */
export interface Target {
  key: string;
  name: string;
  /** 'workspace' is a section heading over that workspace's collections, never
   *  picked: the menus name it only when targets span more than one. */
  kind: 'workspace' | 'collection' | 'group';
  /** Shown on the right instead of the kind, for a row that is an action
   *  rather than a place ("Ungroup"). */
  hint?: string;
  /** A hairline under the row: it separates such an action from the list. */
  divider?: boolean;
}

/*
 * A place this selection cannot go is LEFT OUT of the menu rather than greyed
 * in it, and a button whose menu would be empty is not offered at all: the
 * toolbar shows what can be done, not what cannot.
 */

function TargetMenu({ title, note, targets, onPick }: { title: string; note?: string; targets: Target[]; onPick: (t: Target) => void }) {
  return (
    <MenuSurface bottom={44} left={0} width={300} shadow="menu" maxHeight={400}>
      <MenuLabel>{title}</MenuLabel>
      {note ? <div style={{ margin: '0 6px 4px', padding: '7px 9px', borderRadius: 5, background: '#fafaf9', border: '1px solid #ebebea', fontSize: 12, color: '#6b6b70', lineHeight: 1.4 }}>{note}</div> : null}
      {targets.map((t, i) => t.kind === 'workspace' ? (
        // A hairline between sections, unless the row above already drew one.
        <div key={t.key} className="tr-ellipsis" style={{ padding: '8px 10px 3px', marginTop: i ? 4 : 0, borderTop: i && !targets[i - 1].divider ? '1px solid #ebebea' : undefined, fontSize: 11.5, fontWeight: 600, color: '#8a8a8f' }}>
          {t.name}
        </div>
      ) : (
        <div key={t.key} style={t.divider ? { borderBottom: '1px solid #ebebea', margin: '0 6px 4px', paddingBottom: 4 } : undefined}>
          <div
            className="tr-hover-track"
            onClick={(e) => {
              e.stopPropagation();
              onPick(t);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: `7px 10px 7px ${t.kind === 'group' ? 28 : 10}px`,
              borderRadius: 5,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <span style={{ fontWeight: t.kind === 'collection' ? 500 : 400 }}>{t.name}</span>
            <span style={{ fontSize: 11.5, color: '#8a8a8f' }}>{t.hint ?? t.kind}</span>
          </div>
        </div>
      ))}
    </MenuSurface>
  );
}

export function SelectionToolbar({
  label,
  onSelectAll,
  allSelected,
  moveTargets,
  dupTargets,
  copyWorkspaces,
  moveNote,
  onMove,
  onDuplicate,
  onCopyTo,
  onDelete,
  onClear,
  simple,
}: {
  label: string;
  onSelectAll?: () => void;
  allSelected?: boolean;
  moveTargets?: Target[];
  dupTargets?: Target[];
  /** Workspaces this selection can be copied into whole (the collections list). */
  copyWorkspaces?: Array<{ public_id: string; name: string }>;
  moveNote?: string;
  onMove?: (t: Target) => void;
  onDuplicate?: (t: Target) => void;
  onCopyTo?: (workspace: { public_id: string; name: string }) => void;
  onDelete: () => void;
  onClear: () => void;
  /** the Usage / collections-list version: label, optional Select all, Copy, Delete, Cancel */
  simple?: boolean;
}) {
  const [menu, setMenu] = useState<'move' | 'dup' | null>(null);
  useDismiss(menu !== null, () => setMenu(null));
  const setLift = useToast((s) => s.setLift);
  useEffect(() => {
    setLift(true);
    return () => setLift(false);
  }, [setLift]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (menu) setMenu(null);
        else onClear();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menu, onClear]);

  const divider = <div style={{ width: 1, height: 20, background: '#3a3a3e', margin: '0 6px' }} />;
  const wrap = (children: ReactNode) => (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 52,
        padding: '0 8px 0 16px',
        borderRadius: 12,
        background: '#0f0f10',
        color: '#fff',
        boxShadow: '0 12px 40px rgba(0,0,0,.28)',
        zIndex: 45,
      }}
    >
      {children}
    </div>
  );

  const selectAllLink = onSelectAll ? (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelectAll();
      }}
      style={{ fontSize: 12.5, color: allSelected ? '#6b6b70' : '#fff', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap', padding: '0 6px' }}
    >
      Select all
    </a>
  ) : null;

  const copyMenu = onCopyTo && copyWorkspaces?.length ? (
    <MenuAnchor>
      <Button variant="toolbar" size={36} px={12} fontSize={13} icon={<IconDuplicate size={14} />} onClick={() => setMenu(menu === 'dup' ? null : 'dup')}>
        Copy
      </Button>
      {menu === 'dup' ? (
        <MenuSurface bottom={44} left={0} width={260} shadow="menu" maxHeight={400}>
          <MenuLabel>COPY TO</MenuLabel>
          {copyWorkspaces.map((w) => (
            <MenuItem key={w.public_id} onClick={(e) => { e.stopPropagation(); setMenu(null); onCopyTo(w); }}>{w.name}</MenuItem>
          ))}
        </MenuSurface>
      ) : null}
    </MenuAnchor>
  ) : null;

  if (simple) {
    return wrap(
      <>
        <span style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: 'nowrap', marginRight: 6 }}>{label}</span>
        {selectAllLink}
        {divider}
        {copyMenu}
        <Button variant="toolbarDanger" size={36} px={12} fontSize={13} icon={<IconTrash size={14} />} onClick={onDelete}>
          Delete
        </Button>
        {divider}
        <Button variant="toolbarCancel" size={36} px={12} fontSize={13} title="Cancel selection (Esc)" iconRight={<IconX size={13} sw={2.2} />} onClick={onClear}>
          Cancel
        </Button>
      </>,
    );
  }

  return wrap(
    <>
      <span style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: 'nowrap', marginRight: 6 }}>{label}</span>
      {selectAllLink}
      {divider}
      {onMove && moveTargets?.length ? (
        <MenuAnchor>
          <Button variant="toolbar" size={36} px={12} fontSize={13} icon={<IconArrowRight size={14} />} onClick={() => setMenu(menu === 'move' ? null : 'move')}>
            Move
          </Button>
          {menu === 'move' ? (
            <TargetMenu title="MOVE TO" note={moveNote} targets={moveTargets} onPick={(t) => { setMenu(null); onMove(t); }} />
          ) : null}
        </MenuAnchor>
      ) : null}
      {onDuplicate && dupTargets?.length ? (
        <MenuAnchor>
          <Button variant="toolbar" size={36} px={12} fontSize={13} icon={<IconDuplicate size={14} />} onClick={() => setMenu(menu === 'dup' ? null : 'dup')}>
            Copy
          </Button>
          {menu === 'dup' ? (
            <TargetMenu title="COPY TO" note={moveNote} targets={dupTargets} onPick={(t) => { setMenu(null); onDuplicate(t); }} />
          ) : null}
        </MenuAnchor>
      ) : null}
      <Button variant="toolbarDanger" size={36} px={12} fontSize={13} icon={<IconTrash size={14} />} onClick={onDelete}>
        Delete
      </Button>
      {divider}
      <Button variant="toolbarCancel" size={36} px={12} fontSize={13} title="Cancel selection (Esc)" iconRight={<IconX size={13} sw={2.2} />} onClick={onClear}>
        Cancel
      </Button>
    </>,
  );
}
